import { floor, round } from 'lodash';
import { Vector3 } from 'three';
import { BeaconWeldingData } from '../scratchpad/application/beacon/welding/model/beacon-welding-data';
import { FineTuneAngles } from '../scratchpad/application/shared/model/flow.types';
import { QNear, Waypoint } from './machine-variable.utils';
import { Pose, PoseUtil } from './pose.util';

// Define an interface to represent the fine tune adjustments
export interface FineTuneSettings {
  alongLineOfTravel: number;
  squareToLine: number;
  alongTool: number;
  workAngle: number;
  travelAngle: number;
  axisAngle: number;
}
export interface FineTuneInputs {
  startPoint: Pose;
  endPoint: Pose;
  offset: Pose;
  pointNeeded: FineTunedPointRequired;
}

export interface MultiPassSettings {
  passOffsets: MultipassOffsets;
  passDirection: 'forward' | 'reverse';
  partPosition: WorkPiecePosition;
  referencePass: 'root';
}

export enum AngleModificationMode {
  FIXED = 'fixed',
  RELATIVE = 'relative',
}

export interface MultipassOffsets {
  startAlongPath: number;
  endAlongPath: number;
  lateralToPath: number;
  alongTorchWire: number;
  workAngle: number;
  travelAngle: number;
  mode: AngleModificationMode;
}

export enum FineTunedPointRequired {
  START_POINT = 0,
  MID_POINT = 1,
  END_POINT = 2,
}

// Constants defined for the welding positions
export enum WorkPiecePosition {
  FLAT = 0,
  HORIZONTAL = 1,
  VERTICAL = 2,
  OVERHEAD = 3,
}

export enum WorkPiecePositionName {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
  FLAT = 'flat',
  OVERHEAD = 'overhead',
}

export enum RobotPlane {
  BASE = 0,
  TOOL = 1,
}
export enum RobotPlaneName {
  BASE = 'base',
  TOOL = 'tool',
}

export enum CoordinateName {
  X_AXIS = 'X_AXIS',
  Y_AXIS = 'Y_AXIS',
  Z_AXIS = 'Z_AXIS',
}

// Enhanced Stitch segments
export enum MotionType {
  LINEAR = 'LINEAR',
  RADIAL = 'RADIAL',
  ROTARY = 'ROTARY',
  AIR = 'AIR',
}

export enum ArcSegment {
  FIRST_SEGMENT = 0,
  SECOND_SEGMENT = 1,
}

export interface MotionPoint {
  type: MotionType;
  pose: Pose;
  qnear: QNear;
}

export interface StitchSettings {
  lengthOfStitch: number;
  pitchOfStitch: number;
}

export interface ValidateStitchSettingsResult {
  modifiedSettings: StitchSettings;
  totalSequenceLength: number;
}

interface StitchFitOptions {
  lockPitch?: boolean;
  lockStitchLength?: boolean;
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export class PartUtil {
  public static translatePart(
    initialPointsArray: Array<Pose>,
    startingOrigin: Pose,
    shiftedOrigin: Pose
  ): Array<Pose> {
    // Calculate the difference between the origins
    const differenceOfOrigins = PoseUtil.pose_sub(
      shiftedOrigin,
      startingOrigin
    );

    // Extract the translation data from the difference between the origins
    const translationOfOriginOffset: Pose = [
      differenceOfOrigins[0],
      differenceOfOrigins[1],
      differenceOfOrigins[2],
      0,
      0,
      0,
    ];

    // Apply the translation pose to the array of points which need to be shifted
    return PartUtil.translateToNewOrigin(
      initialPointsArray,
      translationOfOriginOffset
    );
  }

  public static rotatePart(
    initialPointsArray: Array<Pose>,
    startingOrigin: Pose,
    shiftedOrigin: Pose,
    rotationAngle: number,
    rotationAxis: CoordinateName = CoordinateName.Z_AXIS
  ): Array<Pose> {
    const rotationAngleInRadians = rotationAngle * DEG_TO_RAD;
    let rotationPose: Pose = [0, 0, 0, 0, 0, rotationAngleInRadians];
    if (rotationAxis === CoordinateName.X_AXIS) {
      rotationPose = [0, 0, 0, rotationAngleInRadians, 0, 0];
    } else if (rotationAxis === CoordinateName.Y_AXIS) {
      rotationPose = [0, 0, 0, 0, rotationAngleInRadians, 0];
    }
    // Represent the array of points in terms of the coordinate frame of the origin
    const initialPointsInOriginFrame = PartUtil.pointsWRTOriginFrame(
      initialPointsArray,
      startingOrigin
    );

    // Rotate the initial origin accoriding to base coordinates by required amount
    const rotatedOrigin = PartUtil.baseFrameRotation(
      startingOrigin,
      rotationPose
    );

    // Apply the correct axis angles to the new origin
    const modifiedShiftedOrigin: Pose = [
      shiftedOrigin[0],
      shiftedOrigin[1],
      shiftedOrigin[2],
      rotatedOrigin[3],
      rotatedOrigin[4],
      rotatedOrigin[5],
    ];

    // Shift the required points to the new origin
    const shiftedPoints = PartUtil.shiftingPoseToNewOrigin(
      modifiedShiftedOrigin,
      initialPointsInOriginFrame
    );
    return shiftedPoints;
  }

  public static convertWaypointToPose(waypoint: Waypoint): Pose {
    return [
      waypoint.x,
      waypoint.y,
      waypoint.z,
      waypoint.rx,
      waypoint.ry,
      waypoint.rz,
    ];
  }

  public static convertPoseToWaypoint(pose: Pose): Waypoint {
    return {
      x: pose[0],
      y: pose[1],
      z: pose[2],
      rx: pose[3],
      ry: pose[4],
      rz: pose[5],
    };
  }

  public static convertPoseToScriptPose(pose: Pose): string {
    return `p[${[pose[0], pose[1], pose[2], pose[3], pose[4], pose[5]].join(
      ','
    )}]`;
  }

  public static convertWaypointToScriptPose(waypoint: Waypoint): string {
    return `p[${[
      waypoint.x,
      waypoint.y,
      waypoint.z,
      waypoint.rx,
      waypoint.ry,
      waypoint.rz,
    ].join(',')}]`;
  }

  private static translateToNewOrigin(
    pointsArray: Array<Pose>,
    offset: Pose
  ): Array<Pose> {
    const shiftedArray: Array<Pose> = new Array<Pose>(pointsArray.length);
    for (let i = 0; i < pointsArray.length; i++) {
      shiftedArray[i] = PoseUtil.pose_add(offset, pointsArray[i]);
    }
    return shiftedArray;
  }

  private static baseFrameRotation(initialOrigin: Pose, offset: Pose): Pose {
    const originTranslation: Pose = [
      initialOrigin[0],
      initialOrigin[1],
      initialOrigin[2],
      0,
      0,
      0,
    ];
    const originRotation: Pose = [
      0,
      0,
      0,
      initialOrigin[3],
      initialOrigin[4],
      initialOrigin[5],
    ];
    return PoseUtil.pose_add(
      originTranslation,
      PoseUtil.pose_trans(offset, originRotation)
    );
  }

  private static pointsWRTOriginFrame(
    initialPointsArray: Array<Pose>,
    startingOrigin: Pose
  ): Array<Pose> {
    const pointsInOriginFrame: Array<Pose> = new Array<Pose>(
      initialPointsArray.length
    );
    for (let i = 0; i < initialPointsArray.length; i++) {
      pointsInOriginFrame[i] = PoseUtil.pose_trans(
        PoseUtil.pose_inv(startingOrigin),
        initialPointsArray[i]
      );
    }
    return pointsInOriginFrame;
  }

  private static shiftingPoseToNewOrigin(
    origin: Pose,
    points: Array<Pose>
  ): Array<Pose> {
    const shiftedPose: Array<Pose> = new Array<Pose>(points.length);
    for (let i = 0; i < points.length; i++) {
      shiftedPose[i] = PoseUtil.pose_trans(origin, points[i]);
    }
    return shiftedPose;
  }

  /**
   * Translates an array of QNear objects relative to a new origin point.
   * @param initialQnearsArray - The array of QNear objects to translate.
   * @param originQnears - The Qnears of the original origin pose.
   * @param shiftedOriginQnears - The Qnears of the new origin pose to translate
   *  the original Qnears to.
   * @returns The translated array of QNear objects.
   */
  public static translatePartQnears(
    initialQnearsArray: Array<QNear>,
    originQnears: QNear,
    shiftedOriginQnears: QNear
  ): Array<QNear> {
    // Calculate the delta of the Origin Joint angles and the Joint angles of
    // each Point selected
    const initialQnearDelta = this.getQnearDelta(
      initialQnearsArray,
      originQnears
    );

    // Apply the obtained delta to the new shifted origin
    return this.shiftQnearDelta(shiftedOriginQnears, initialQnearDelta);
  }

  /**
   * Rotates an array of QNear objects relative to a rotation origin point.
   * This function applies rotation to joint angles by adding rotation offsets.
   * @param initialQnearsArray - The array of QNear objects to rotate.
   * @param originQnears - The Qnears of the original origin pose.
   * @param rotatedOriginQnears - The Qnears of the rotated origin pose.
   * @param rotationAngle - The rotation angle in degrees.
   * @param rotationAxis - The axis of rotation (defaults to Z_AXIS).
   * @returns The rotated array of QNear objects.
   */
  public static rotatePartQnears(
    initialQnearsArray: Array<QNear>,
    originQnears: QNear,
    rotatedOriginQnears: QNear,
    rotationAngle: number,
    rotationAxis: CoordinateName = CoordinateName.Z_AXIS
  ): Array<QNear> {
    // Calculate the delta of the Origin Joint angles and the Joint angles of
    // each Point selected
    const initialQnearDelta = this.getQnearDelta(
      initialQnearsArray,
      originQnears
    );

    // Apply rotation transformation to the delta
    const rotatedQnearDelta = initialQnearDelta.map((qnearDelta) =>
      this.applyQnearRotation(qnearDelta, rotationAngle, rotationAxis)
    );

    // Apply the rotated delta to the rotated origin
    const rotatedQnears = this.shiftQnearDelta(
      rotatedOriginQnears,
      rotatedQnearDelta
    );
    return rotatedQnears;
  }

  /**
   * Applies rotation transformation to a single QNear based on rotation angle and axis.
   * For joint angles, rotation involves adding the rotation angle to the appropriate joint.
   * @param qnear - The QNear to rotate.
   * @param rotationAngle - The rotation angle in degrees.
   * @param rotationAxis - The axis of rotation.
   * @returns The rotated QNear.
   */
  private static applyQnearRotation(
    qnear: QNear,
    rotationAngle: number,
    rotationAxis: CoordinateName
  ): QNear {
    const [j1, j2, j3, j4, j5, j6] = qnear;
    const rotationAngleInRadians = rotationAngle * DEG_TO_RAD;

    const rotatedJ1 = j1;
    const rotatedJ2 = j2;
    const rotatedJ3 = j3;
    let rotatedJ4 = j4;
    let rotatedJ5 = j5;
    let rotatedJ6 = j6;

    switch (rotationAxis) {
      case CoordinateName.X_AXIS:
        // Rotate around X-axis: primarily affects J4 (roll)
        rotatedJ4 = j4 + rotationAngleInRadians;
        break;

      case CoordinateName.Y_AXIS:
        // Rotate around Y-axis: primarily affects J5 (pitch)
        rotatedJ5 = j5 + rotationAngleInRadians;
        break;

      case CoordinateName.Z_AXIS:
        // Rotate around Z-axis: primarily affects J6 (yaw)
        rotatedJ6 = j6 + rotationAngleInRadians;
        break;
    }

    const rotatedQnear: QNear = [
      rotatedJ1,
      rotatedJ2,
      rotatedJ3,
      rotatedJ4,
      rotatedJ5,
      rotatedJ6,
    ];

    // Apply angle constraints with padding
    return this.constrainQnearAngles(rotatedQnear);
  }

  /**
   * Constrains QNear joint angles within safe limits with padding.
   * Limits are -360 to +360 degrees with padding to avoid getting too close to limits.
   * @param qnear - The QNear to constrain.
   * @param paddingDegrees - Padding from limits in degrees (default: 10 degrees).
   * @returns The constrained QNear.
   */
  private static constrainQnearAngles(
    qnear: QNear,
    paddingDegrees: number = 10
  ): QNear {
    const MAX_ANGLE_DEGREES = 360;
    const MIN_ANGLE_DEGREES = -360;
    const MAX_ANGLE_RADIANS = MAX_ANGLE_DEGREES * DEG_TO_RAD;
    const MIN_ANGLE_RADIANS = MIN_ANGLE_DEGREES * DEG_TO_RAD;
    const PADDING_RADIANS = paddingDegrees * DEG_TO_RAD;

    const constrainedQnear: QNear = [
      this.constrainAngle(
        qnear[0],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
      this.constrainAngle(
        qnear[1],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
      this.constrainAngle(
        qnear[2],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
      this.constrainAngle(
        qnear[3],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
      this.constrainAngle(
        qnear[4],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
      this.constrainAngle(
        qnear[5],
        MAX_ANGLE_RADIANS,
        MIN_ANGLE_RADIANS,
        PADDING_RADIANS
      ),
    ] as QNear;

    return constrainedQnear;
  }

  /**
   * Constrains a single angle within safe limits with padding.
   * @param angle - The angle in radians to constrain.
   * @param maxAngle - The maximum allowed angle in radians.
   * @param minAngle - The minimum allowed angle in radians.
   * @param padding - The padding from limits in radians.
   * @returns The constrained angle in radians.
   */
  private static constrainAngle(
    angle: number,
    maxAngle: number,
    minAngle: number,
    padding: number
  ): number {
    // Normalize angle to be within -2π to +2π range
    let normalizedAngle = angle;
    while (normalizedAngle > Math.PI * 2) {
      normalizedAngle = Math.PI * 2;
    }
    while (normalizedAngle < -Math.PI * 2) {
      normalizedAngle = -Math.PI * 2;
    }

    // Apply constraints with padding
    if (normalizedAngle > maxAngle - padding) {
      normalizedAngle = maxAngle - padding;
    } else if (normalizedAngle < minAngle + padding) {
      normalizedAngle = minAngle + padding;
    }

    return normalizedAngle;
  }

  public static getQnearDelta(
    initialQnears: Array<QNear>,
    originQnear: QNear
  ): Array<QNear> {
    // calculate the delta of Qnear of each point to origin
    return initialQnears.map((qnear) => PoseUtil.qnear_sub(qnear, originQnear));
  }

  public static shiftQnearDelta(
    shiftedOriginQnear: QNear,
    qnearDeltaArray: Array<QNear>
  ): Array<QNear> {
    // Apply the delta of QNears to the new origin
    return qnearDeltaArray.map((qnearDelta) =>
      PoseUtil.qnear_add(qnearDelta, shiftedOriginQnear)
    );
  }

  // Fine Tune functions

  /**
   * Returns a final pose with the desired fine tuned offsets
   * @param startPose The first pose forming the arc travel path
   * @param endPose The second pose forming the arc travel path
   * @param fineTuneData FineTuneSettings interface
   * @returns Pose which indicates the robot position at modified pose
   */
  public static getFineTunedPoseToPath_linear(
    startPose: Pose,
    endPose: Pose,
    fineTuneData: FineTuneSettings,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    reverseOffset?: boolean
  ): Pose {
    // Constants
    const isStartPoint = pointRequired === FineTunedPointRequired.START_POINT;
    const originalSquareToLine = fineTuneData.squareToLine;
    const originalWorkAngle = fineTuneData.workAngle;
    let shouldInvertOffset = PoseUtil.isLineRotated(startPose, endPose);

    partPosition = this.inferWorkPiecePosition(startPose, endPose);
    if (partPosition === WorkPiecePosition.VERTICAL) {
      shouldInvertOffset = isStartPoint;
      if (reverseOffset) {
        shouldInvertOffset = !shouldInvertOffset;
      }
    }

    let adjustedData: FineTuneSettings = { ...fineTuneData };
    if (isStartPoint && shouldInvertOffset) {
      adjustedData = {
        ...adjustedData,
        workAngle: -adjustedData.workAngle,
        squareToLine: -adjustedData.squareToLine,
      };
    }
    if (!isStartPoint) {
      adjustedData = this.invertData(adjustedData);
      if (!shouldInvertOffset) {
        adjustedData.squareToLine = originalSquareToLine;
        adjustedData.workAngle = originalWorkAngle;
      }
    }
    // Fine-tune the pose based on the adjusted fineTuneData
    const fineTunedPose = this.fineTunePoint(
      startPose,
      endPose,
      adjustedData,
      FineTunedPointRequired.START_POINT
    );
    return fineTunedPose;
  }

  /**
   * Returns a final pose with the desired fine tuned offsets
   * @param startPose The first pose forming the arc travel path
   * @param midPose The second pose forming the arc travel path
   * @param endPose The second pose forming the arc travel path
   * @param fineTuneData FineTuneSettings interface
   * @returns Pose which indicates the robot position at modified pose
   */
  public static getFineTunedPoseToPath_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    fineTuneData: FineTuneSettings,
    pointRequired: FineTunedPointRequired,
    _partPosition?: WorkPiecePosition
  ): Pose {
    // Determine the point to modify
    const startingPose: Pose = this.determinePointToModify(
      startPose,
      midPose,
      endPose,
      pointRequired
    );
    // Calculate a pose along the travel path: apply translation offset
    const travelPathOffset = fineTuneData.alongLineOfTravel / 1000;
    const tangentPose = this.interpolatePoseOnArc(
      startPose,
      midPose,
      endPose,
      pointRequired,
      travelPathOffset
    );
    // Invert work angle property if line is rotated
    let shouldInvertAngle = PoseUtil.isLineRotated(startingPose, tangentPose);
    if (travelPathOffset != 0) {
      shouldInvertAngle = PoseUtil.isLineRotated(tangentPose, startingPose);
      if (travelPathOffset > 0) {
        fineTuneData.travelAngle = -fineTuneData.travelAngle;
      }
    }
    if (shouldInvertAngle) {
      fineTuneData.workAngle = -fineTuneData.workAngle;
      fineTuneData.squareToLine = -fineTuneData.squareToLine;
    }
    // Zero out translation to allow only rotation fine-tune on point
    fineTuneData.alongLineOfTravel = 0;

    const firstPose = travelPathOffset !== 0 ? tangentPose : startingPose;
    const secondPose = travelPathOffset !== 0 ? startingPose : tangentPose;
    // Apply fine-tune rotation offsets to original point
    const fineTuned_pose = this.fineTunePoint(
      firstPose,
      secondPose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTuned_pose;
  }

  private static determinePointToModify(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired
  ): Pose {
    if (pointRequired === FineTunedPointRequired.START_POINT) {
      return startPose;
    } else if (pointRequired === FineTunedPointRequired.MID_POINT) {
      return midPose;
    }
    return endPose;
  }

  public static getFineTunedPoseInTool(
    startPose: Pose,
    fineTuneData: FineTuneSettings
  ): Pose {
    // Convert the fine-tune array to a Pose
    const offsetPose = this.convertFineTuneSettingsToPose(fineTuneData);
    return PoseUtil.pose_trans(startPose, offsetPose);
  }

  /**
   * Fine-tunes the position of the required poses along a given direction by
   *  a given offset.
   * @param poses The array of poses to be modified with an offset.
   * @param FineTuneOffset The required offset.
   * @returns the fine-tuned array of poses
   */

  public static fineTuneSetOfPoints(
    poses: Array<Pose>,
    FineTuneOffset: FineTuneSettings
  ): Array<Pose> {
    // define an array of length same as input array
    const modifiedPoseArray: Array<Pose> = new Array<Pose>(poses.length);

    // Iterate through the loop of array of poses and apply offset to all points
    for (let i = 0; i < poses.length; i++) {
      if (i === poses.length - 1) {
        modifiedPoseArray[i] = this.fineTunePoint(
          poses[i - 1],
          poses[i],
          FineTuneOffset,
          FineTunedPointRequired.END_POINT
        );
      } else {
        modifiedPoseArray[i] = this.fineTunePoint(
          poses[i],
          poses[i + 1],
          FineTuneOffset,
          FineTunedPointRequired.START_POINT
        );
      }
    }
    return modifiedPoseArray;
  }

  /**
   * Fine-tunes the position of two beginning poses along a given direction
   *  by a given offset.
   * @param startPose The starting pose.
   * @param endPose The end pose.
   * @param fineTuneData The object containing all the information regarding
   *  fine tune adjustments
   * @param isEnd Boolean value signifying if endPose needs to be modified
   * @returns the fine-tuned starting pose
   */
  public static fineTunePoint(
    startPose: Pose,
    endPose: Pose,
    fineTuneData: FineTuneSettings,
    desiredPoint:
      | FineTunedPointRequired.START_POINT
      | FineTunedPointRequired.END_POINT
  ): Pose {
    // Convert the ArrowAction array to a Pose
    const offsetPose = this.convertFineTuneSettingsToPose(fineTuneData);

    const fineTuneinput: FineTuneInputs = {
      startPoint: startPose,
      endPoint: endPose,
      offset: offsetPose,
      pointNeeded: desiredPoint,
    };

    // Calculate the modified pose between the start and end poses, with
    // the fine - tune offset applied.
    const modifiedPose = this.fineTuneMode(fineTuneinput);
    // Return the modified startPose
    return modifiedPose;
  }

  /**
   * Lower level function to apply an offset to a pose wrt a calculated
   *  frame of reference.
   * @param fineTuneinput The object the two points and an offset to be applied
   * @returns the fine-tuned starting pose
   */
  public static fineTuneMode(fineTuneinput: FineTuneInputs): Pose {
    // Feature plane origin with X-Axis along direction of travel between
    // the two waypoints
    const rotatedFeaturePose = PoseUtil.getCustomFeaturePlaneOrigin(
      fineTuneinput.startPoint,
      fineTuneinput.endPoint
    );
    const offsetFeatureOrigin = PoseUtil.pose_trans(
      rotatedFeaturePose,
      fineTuneinput.offset
    );

    const deltaPose_startPoint = PoseUtil.pose_sub(
      fineTuneinput.startPoint,
      rotatedFeaturePose
    );

    const deltaPose_endPoint = PoseUtil.pose_sub(
      fineTuneinput.endPoint,
      rotatedFeaturePose
    );

    const newPose_1 = PoseUtil.pose_add(
      offsetFeatureOrigin,
      deltaPose_startPoint
    );

    const newPose_2 = PoseUtil.pose_add(
      offsetFeatureOrigin,
      deltaPose_endPoint
    );
    return fineTuneinput.pointNeeded === FineTunedPointRequired.END_POINT
      ? newPose_2
      : newPose_1;
  }

  /**
   * Convert the arrowActions Array to represent the position and orientation
   *  of a robot pose
   * @param fineTuneData The object containing all the information regarding
   *  fine tune adjustments
   * @returns A Pose
   */
  public static convertFineTuneSettingsToPose(
    offsetValues: FineTuneSettings
  ): Pose {
    const pose: Pose = [
      offsetValues.alongLineOfTravel / 1000,
      offsetValues.squareToLine / 1000,
      offsetValues.alongTool / 1000,
      offsetValues.workAngle * DEG_TO_RAD,
      offsetValues.travelAngle * DEG_TO_RAD,
      offsetValues.axisAngle * DEG_TO_RAD,
    ];
    return pose;
  }

  public static getTravelAngle_linear(
    startPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired
  ): number {
    return this.getAngleOfPerpToTravelAxis_linear(
      startPose,
      endPose,
      pointRequired
    );
  }

  public static getTravelAngle_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired
  ): number {
    return this.getAngleOfPerpToTravelAxis_radial(
      startPose,
      midPose,
      endPose,
      pointRequired
    );
  }

  public static shouldModifyAngleCalculation(
    pose1: Pose,
    pose2: Pose
  ): boolean {
    const CORNER_RATIO_LIMIT = 20;
    const cornerRatio = PoseUtil.getRatioBetweenTwoPoses(pose1, pose2);
    if (cornerRatio > CORNER_RATIO_LIMIT) {
      return true;
    }
    return false;
  }

  public static setTravelAngle_linear(
    pose1: Pose,
    pose2: Pose,
    traveAngleOffset: number,
    pointRequired: FineTunedPointRequired
  ): Pose {
    if (pointRequired === FineTunedPointRequired.END_POINT) {
      traveAngleOffset = -traveAngleOffset;
    }
    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      travelAngle: traveAngleOffset,
      workAngle: 0,
      axisAngle: 0,
      alongTool: 0,
    };
    const modifiedPose = this.fineTunePoint(
      pose1,
      pose2,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return modifiedPose;
  }

  public static setTravelAngle_radial(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    travelAngleOffset: number,
    pointRequired: FineTunedPointRequired
  ): Pose {
    let starting_pose = pose1;
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      starting_pose = pose2;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      starting_pose = pose3;
      travelAngleOffset = -travelAngleOffset;
    }
    const tangent_pose = this.calculateArcTangentPose(
      pose1,
      pose2,
      pose3,
      pointRequired
    );
    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      travelAngle: travelAngleOffset,
      workAngle: 0,
      axisAngle: 0,
      alongTool: 0,
    };
    const fineTunedPose = this.fineTunePoint(
      starting_pose,
      tangent_pose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTunedPose;
  }

  public static getWorkAngle_linear(
    startPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): number {
    let firstPose = startPose;
    let secondPose = endPose;
    if (pointRequired === FineTunedPointRequired.END_POINT) {
      firstPose = endPose;
      secondPose = startPose;
    }
    const workAngle = this.calculateTorchAngleToWorkPiece(
      firstPose,
      secondPose,
      partPosition,
      planeInclineOffset
    );

    return floor(workAngle);
  }

  public static getWorkAngle_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): number {
    const tangentPose = this.calculateArcTangentPose(
      startPose,
      midPose,
      endPose,
      pointRequired
    );
    let starting_pose = startPose;
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      starting_pose = midPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      starting_pose = endPose;
    }
    const workAngle = this.calculateTorchAngleToWorkPiece(
      starting_pose,
      tangentPose,
      partPosition,
      planeInclineOffset
    );
    return floor(workAngle);
  }

  public static setWorkAngle_linear(
    startPose: Pose,
    endPose: Pose,
    workAngleOffset: number,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    _planeInclineOffset = 0
  ): Pose {
    const isStartPoint = pointRequired === FineTunedPointRequired.START_POINT;
    // Invert the work angle if line is rotated
    let shouldInvertAngle = PoseUtil.isLineRotated(startPose, endPose);
    if (partPosition === WorkPiecePosition.VERTICAL) {
      shouldInvertAngle = isStartPoint;
    }
    // Determine settings for fine-tune
    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      travelAngle: 0,
      workAngle: shouldInvertAngle ? -workAngleOffset : workAngleOffset,
      axisAngle: 0,
      alongTool: 0,
    };

    const fineTunedPose = this.fineTunePoint(
      startPose,
      endPose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTunedPose;
  }

  public static setWorkAngle_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    workAngleOffset: number,
    pointRequired: FineTunedPointRequired
  ): Pose {
    let starting_pose = startPose;
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      starting_pose = midPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      starting_pose = endPose;
    }
    const tangent_pose = this.calculateArcTangentPose(
      startPose,
      midPose,
      endPose,
      pointRequired
    );

    // Invert the work angle if line is rotated
    const shouldInvertAngle = PoseUtil.isLineRotated(
      starting_pose,
      tangent_pose
    );

    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      travelAngle: 0,
      workAngle: shouldInvertAngle ? -workAngleOffset : workAngleOffset,
      axisAngle: 0,
      alongTool: 0,
    };
    const fineTunedPose = this.fineTunePoint(
      starting_pose,
      tangent_pose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTunedPose;
  }

  public static setDesiredWorkAngle_linear(
    startPose: Pose,
    endPose: Pose,
    desiredWorkAngle: number,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): Pose {
    const currentWorkAngle = this.getWorkAngle_linear(
      startPose,
      endPose,
      pointRequired,
      partPosition,
      planeInclineOffset
    );
    const diffAngle = desiredWorkAngle - currentWorkAngle;
    const fineTunedPose = this.setWorkAngle_linear(
      startPose,
      endPose,
      diffAngle,
      pointRequired
    );
    return fineTunedPose;
  }

  public static setDesiredWorkAngle_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    desiredWorkAngle: number,
    pointRequired: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): Pose {
    const currentWorkAngle = this.getWorkAngle_radial(
      startPose,
      midPose,
      endPose,
      pointRequired,
      partPosition,
      planeInclineOffset
    );
    const diffAngle = desiredWorkAngle - currentWorkAngle;
    const fineTunedPose = this.setWorkAngle_radial(
      startPose,
      midPose,
      endPose,
      diffAngle,
      pointRequired
    );
    return fineTunedPose;
  }

  public static getCurrentTorchAngles_linear(
    pose1: Pose,
    pose2: Pose,
    requiredPoint: FineTunedPointRequired,
    prevPose?: Pose,
    partPosition?: WorkPiecePosition,
    planeAngle = 0,
    mode?: AngleModificationMode
  ): FineTuneAngles {
    if (mode === AngleModificationMode.RELATIVE) {
      partPosition = this.inferWorkPiecePosition(pose1, pose2);
    }
    // Get Travel angle of pose
    let travel_angle = this.getRotationOfPerpToTravelAxis(pose1, pose2);
    // Get Work angle of pose
    const workAngle = this.calculateTorchAngleToWorkPiece(
      pose1,
      pose2,
      partPosition,
      planeAngle
    );

    if (requiredPoint === FineTunedPointRequired.END_POINT) {
      travel_angle = -travel_angle;
    }
    return {
      travelAngle: floor(travel_angle),
      workAngle: floor(workAngle),
    };
  }

  public static getCurrentTorchAngles_radial(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    requiredPoint: FineTunedPointRequired,
    partPosition?: WorkPiecePosition,
    planeAngle = 0
  ): FineTuneAngles {
    // Get Travel angle of pose
    const travel_angle = this.getTravelAngle_radial(
      pose1,
      pose2,
      pose3,
      requiredPoint
    );
    // Get Work angle of pose
    const workAngle = this.getWorkAngle_radial(
      pose1,
      pose2,
      pose3,
      requiredPoint,
      partPosition,
      planeAngle
    );
    return {
      travelAngle: floor(travel_angle),
      workAngle: floor(workAngle),
    };
  }

  public static setDesiredTorchAngles_linear(
    pose1: Pose,
    pose2: Pose,
    pointRequired: FineTunedPointRequired,
    prevPose?: Pose,
    desiredTravelAngle?: number,
    desiredWorkAngle?: number,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): Pose {
    let diffTravelAngle = 0;
    let diffWorkAngle = 0;
    if (
      this.shouldModifyAngleCalculation(pose1, pose2) &&
      prevPose !== undefined
    ) {
      pose2 = prevPose;
      pointRequired = FineTunedPointRequired.END_POINT;
    }
    const currentAngles = this.getCurrentTorchAngles_linear(
      pose1,
      pose2,
      pointRequired,
      prevPose,
      partPosition,
      planeInclineOffset
    );
    if (desiredTravelAngle != undefined) {
      diffTravelAngle = desiredTravelAngle - (currentAngles.travelAngle ?? 0);
    }
    if (desiredWorkAngle != undefined) {
      diffWorkAngle = desiredWorkAngle - (currentAngles.workAngle ?? 0);
      if (partPosition === WorkPiecePosition.OVERHEAD) {
        diffWorkAngle = -diffWorkAngle;
      }
    }

    //set travel angle function
    const modifiedPose = this.setTravelAngle_linear(
      pose1,
      pose2,
      diffTravelAngle,
      pointRequired
    );
    //set work angle function
    const fineTunedPose = this.setWorkAngle_linear(
      modifiedPose,
      pose2,
      diffWorkAngle,
      pointRequired,
      partPosition,
      planeInclineOffset
    );

    return fineTunedPose;
  }

  public static setDesiredTorchAngles_radial(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    pointRequired: FineTunedPointRequired,
    desiredTravelAngle?: number,
    desiredWorkAngle?: number,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0
  ): Pose {
    let diffTravelAngle = 0;
    let diffWorkAngle = 0;

    const currentAngles = this.getCurrentTorchAngles_radial(
      pose1,
      pose2,
      pose3,
      pointRequired,
      partPosition,
      planeInclineOffset
    );
    if (desiredTravelAngle != undefined) {
      diffTravelAngle = desiredTravelAngle - (currentAngles.travelAngle ?? 0);
    }
    if (desiredWorkAngle != undefined) {
      diffWorkAngle = desiredWorkAngle - (currentAngles.workAngle ?? 0);
      if (partPosition === WorkPiecePosition.OVERHEAD) {
        diffWorkAngle = -diffWorkAngle;
      }
    }
    //set travel angle function
    const modifiedPose = this.setTravelAngle_radial(
      pose1,
      pose2,
      pose3,
      diffTravelAngle,
      pointRequired
    );
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      pose2 = modifiedPose;
    } else if (pointRequired === FineTunedPointRequired.START_POINT) {
      pose1 = modifiedPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      pose3 = modifiedPose;
    }
    //set work angle function
    const fineTunedPose = this.setWorkAngle_radial(
      pose1,
      pose2,
      pose3,
      diffWorkAngle,
      pointRequired
    );
    return fineTunedPose;
  }

  public static setTorchAngleOffsets_linear(
    pose1: Pose,
    pose2: Pose,
    pointRequired: FineTunedPointRequired,
    travelAngleOffset?: number,
    workAngleOffset?: number,
    partPosition?: WorkPiecePosition,
    prevPose?: Pose,
    mode?: AngleModificationMode,
    travelDirection = 'forward'
  ): Pose {
    if (
      this.shouldModifyAngleCalculation(pose1, pose2) &&
      prevPose !== undefined
    ) {
      pose2 = prevPose;
      pointRequired = FineTunedPointRequired.END_POINT;
    }
    if (mode === AngleModificationMode.RELATIVE) {
      partPosition = this.inferWorkPiecePosition(pose1, pose2);
    }
    // Invert the work angle if line is rotated
    if (partPosition === WorkPiecePosition.OVERHEAD) {
      workAngleOffset = -(workAngleOffset ?? 0);
    } else if (
      partPosition === WorkPiecePosition.VERTICAL &&
      travelDirection !== 'forward'
    ) {
      workAngleOffset = -(workAngleOffset ?? 0);
    }

    //set travel angle function
    const modifiedPose = this.setTravelAngle_linear(
      pose1,
      pose2,
      travelAngleOffset ?? 0,
      pointRequired
    );
    //set work angle function
    const fineTunedPose = this.setWorkAngle_linear(
      modifiedPose,
      pose2,
      workAngleOffset ?? 0,
      pointRequired,
      partPosition
    );

    return fineTunedPose;
  }

  public static setTorchAngleOffsets_radial(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    pointRequired: FineTunedPointRequired,
    travelAngleOffset?: number,
    workAngleOffset?: number,
    partPosition?: WorkPiecePosition
  ): Pose {
    if (partPosition === WorkPiecePosition.OVERHEAD) {
      workAngleOffset = -(workAngleOffset ?? 0);
    }
    //set travel angle function
    const modifiedPose = this.setTravelAngle_radial(
      pose1,
      pose2,
      pose3,
      travelAngleOffset ?? 0,
      pointRequired
    );
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      pose2 = modifiedPose;
    } else if (pointRequired === FineTunedPointRequired.START_POINT) {
      pose1 = modifiedPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      pose3 = modifiedPose;
    }
    //set work angle function
    const fineTunedPose = this.setWorkAngle_radial(
      pose1,
      pose2,
      pose3,
      workAngleOffset ?? 0,
      pointRequired
    );
    return fineTunedPose;
  }

  public static calculateTorchAngleToWorkPiece(
    pose1: Pose,
    pose2: Pose,
    partPosition?: WorkPiecePosition,
    planeInclineOffset = 0,
    _referencePlane?: CoordinateName
  ): number {
    // Define constants
    const COORDINATE_OFFSET = 90 * DEG_TO_RAD;
    const YZ_PLANE: Vector3 = new Vector3(-1, 0, 0);
    // create new frame of reference on based on XY plane at origin
    const refPlane_Z_axis = PoseUtil.pose_add([0, 0, 0.05, 0, 0, 0], pose1);
    const refPose_alongPlaneX: Pose = [
      pose2[0],
      pose2[1],
      pose1[2],
      pose2[3],
      pose2[4],
      pose2[5],
    ];
    const refPose_alongPlaneZ: Pose = [
      pose1[0],
      pose1[1],
      pose2[2],
      pose1[3],
      pose1[4],
      pose1[5],
    ];
    // infer weld position if undefined
    if (partPosition === undefined) {
      partPosition = this.inferWorkPiecePosition(pose1, pose2);
    }
    // Calculate line origin and deltas
    let lineOrigin = PoseUtil.getCustomFeaturePlaneOrigin(
      pose1,
      refPose_alongPlaneX
    );
    if (partPosition === WorkPiecePosition.VERTICAL) {
      lineOrigin = PoseUtil.getCustomFeaturePlaneOrigin(
        pose1,
        refPose_alongPlaneZ
      );
    }
    const lineOrigin_z_vector = PoseUtil.extractVectorFromPose(
      lineOrigin,
      CoordinateName.Z_AXIS
    );

    const refPlaneAtLineOrigin = PoseUtil.pose_constructPlane(
      pose1,
      refPose_alongPlaneX,
      refPlane_Z_axis
    );
    const refPlane_z_vector = PoseUtil.extractVectorFromPose(
      refPlaneAtLineOrigin,
      CoordinateName.Z_AXIS
    );
    const calculatedAngle = PoseUtil.calculateAngleBetweenVectors(
      lineOrigin_z_vector,
      refPlane_z_vector
    );
    let angleToWorkPiece = calculatedAngle;
    if (PoseUtil.isLineRotated(pose1, pose2)) {
      angleToWorkPiece = COORDINATE_OFFSET * 2 - calculatedAngle;
    }
    // Check weld position data
    if (partPosition === WorkPiecePosition.VERTICAL) {
      const planeNormal: Vector3 = new Vector3(0, 0, 1);
      angleToWorkPiece = PoseUtil.calculateAngleBetweenVectors360(
        lineOrigin_z_vector,
        YZ_PLANE,
        planeNormal
      );
    } else if (partPosition === WorkPiecePosition.OVERHEAD) {
      angleToWorkPiece = COORDINATE_OFFSET - calculatedAngle;
    }
    // add plane offset to angle
    angleToWorkPiece =
      round(angleToWorkPiece * RAD_TO_DEG, 0) - planeInclineOffset;
    return angleToWorkPiece;
  }

  /**
   * calculate current angle of rotation of the axis perpendicular
   * to travel path between linear poses
   * @param startPose The first pose forming the travel path
   * @param endPose The second pose forming the travel path
   * @param pointRequired Specify which point needs to be returned
   * @returns push/pull angle: +ve value is PUSH, -ve value is PULL
   */
  public static getAngleOfPerpToTravelAxis_linear(
    startPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired
  ): number {
    let travel_angle = this.getRotationOfPerpToTravelAxis(startPose, endPose);
    if (pointRequired === FineTunedPointRequired.END_POINT) {
      travel_angle = -travel_angle;
    }
    return round(travel_angle, 0);
  }

  /**
   * calculate current angle of rotation of the axis perpendicular
   * to travel path between radial poses
   * @param startPose The first pose forming the arc travel path
   * @param midPose The second pose forming the arc travel path
   * @param endPose The third pose forming the arc travel path
   * @param pointRequired Specify which point needs to be returned
   * @returns push/pull angle: +ve value is PUSH, -ve value is PULL
   */
  public static getAngleOfPerpToTravelAxis_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired
  ): number {
    const tangentPose = this.calculateArcTangentPose(
      startPose,
      midPose,
      endPose,
      pointRequired
    );
    let starting_pose = startPose;
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      starting_pose = midPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      starting_pose = endPose;
    }

    let travel_angle = this.getRotationOfPerpToTravelAxis(
      starting_pose,
      tangentPose
    );
    if (pointRequired === FineTunedPointRequired.END_POINT) {
      travel_angle = -travel_angle;
    }
    return round(travel_angle, 0);
  }

  /**
   * Returns a final pose with the desired angle
   * @param startPose The first pose forming the arc travel path
   * @param endPose The second pose forming the arc travel path
   * @param desiredRotationAngle Final Travel angle needed by user
   * @param pointRequired Specify which point needs to be returned:Start, Mid, End
   * @returns Pose which indicates the robot position at desired travel angle
   */
  public static setAngleOfPerpAxisToPath_linear(
    startPose: Pose,
    endPose: Pose,
    desiredRotationAngle: number,
    desiredPoint:
      | FineTunedPointRequired.START_POINT
      | FineTunedPointRequired.END_POINT
  ): Pose {
    const currentRotationAngle = this.getAngleOfPerpToTravelAxis_linear(
      startPose,
      endPose,
      desiredPoint
    );
    let diffAngle = desiredRotationAngle - currentRotationAngle;
    if (desiredPoint === FineTunedPointRequired.END_POINT) {
      diffAngle = -diffAngle;
    }
    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      alongTool: 0,
      travelAngle: diffAngle,
      workAngle: 0,
      axisAngle: 0,
    };
    const fineTunedPose = this.fineTunePoint(
      startPose,
      endPose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTunedPose;
  }

  /**
   * Returns a final pose with the desired travel angle
   * @param startPose The first pose forming the arc travel path
   * @param midPose The second pose forming the arc travel path
   * @param endPose The third pose forming the arc travel path
   * @param desiredRotationAngle Final Travel angle needed by user
   * @param pointRequired Specify which point needs to be returned:Start, Mid, End
   * @returns Pose which indicates the robot position at desired travel angle
   */
  public static setAngleOfPerpAxisToPath_radial(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    desiredRotationAngle: number,
    pointRequired: FineTunedPointRequired
  ): Pose {
    const currentRotationAngle = this.getAngleOfPerpToTravelAxis_radial(
      startPose,
      midPose,
      endPose,
      pointRequired
    );
    let diffAngle = desiredRotationAngle - currentRotationAngle;

    let starting_pose = startPose;
    if (pointRequired === FineTunedPointRequired.MID_POINT) {
      starting_pose = midPose;
    } else if (pointRequired === FineTunedPointRequired.END_POINT) {
      starting_pose = endPose;
      diffAngle = -diffAngle;
    }
    const tangent_pose = this.calculateArcTangentPose(
      startPose,
      midPose,
      endPose,
      pointRequired
    );

    const fineTuneData: FineTuneSettings = {
      squareToLine: 0,
      alongLineOfTravel: 0,
      alongTool: 0,
      travelAngle: diffAngle,
      workAngle: 0,
      axisAngle: 0,
    };
    const fineTuned_pose = this.fineTunePoint(
      starting_pose,
      tangent_pose,
      fineTuneData,
      FineTunedPointRequired.START_POINT
    );
    return fineTuned_pose;
  }

  /**
   * Calculate the angle between the Z axis vector of the start pose and the
   * Z axis vector of the end pose
   */
  private static getRotationOfPerpToTravelAxis(
    startPose: Pose,
    endPose: Pose,
    pointRequired = FineTunedPointRequired.START_POINT
  ): number {
    // define constants
    const AXIS_OFFSET = 1.57;

    // Calculate line origin and deltas
    const lineOrigin = PoseUtil.getCustomFeaturePlaneOrigin(startPose, endPose);
    const lineOrigin_x_vector = PoseUtil.extractVectorFromPose(
      lineOrigin,
      CoordinateName.X_AXIS
    );
    const lineOrigin_z_vector = PoseUtil.extractVectorFromPose(
      lineOrigin,
      CoordinateName.Z_AXIS
    );

    // Extract vectors from start and end pose
    const startPose_z_vector = PoseUtil.extractVectorFromPose(
      startPose,
      CoordinateName.Z_AXIS
    );
    const endPose_z_vector = PoseUtil.extractVectorFromPose(
      endPose,
      CoordinateName.Z_AXIS
    );

    // calculate angle between vectors
    let rotationOfStartPose = PoseUtil.calculateAngleBetweenVectors(
      lineOrigin_z_vector,
      startPose_z_vector
    );
    let rotationOfEndPose = PoseUtil.calculateAngleBetweenVectors(
      lineOrigin_z_vector,
      endPose_z_vector
    );
    // Find travel angle direction
    const lineOriginDiff_start = PoseUtil.calculateAngleBetweenVectors(
      lineOrigin_x_vector,
      startPose_z_vector
    );
    const lineOriginDiff_end = PoseUtil.calculateAngleBetweenVectors(
      lineOrigin_x_vector,
      endPose_z_vector
    );
    if (lineOriginDiff_start > AXIS_OFFSET) {
      rotationOfStartPose = -rotationOfStartPose;
    } else if (lineOriginDiff_end > AXIS_OFFSET) {
      rotationOfEndPose = -rotationOfEndPose;
    }

    // Convert radians to degrees
    if (pointRequired == FineTunedPointRequired.END_POINT) {
      return rotationOfEndPose * RAD_TO_DEG;
    } else {
      return rotationOfStartPose * RAD_TO_DEG;
    }
  }

  /**
   * Calculate a tangent pose on the radial curve
   * created by 3 poses
   * Returns a tangent pose next to the required point
   */
  public static calculateArcTangentPose(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired,
    arcDistance = 0
  ): Pose {
    let tangentPose: Pose = [0, 0, 0, 0, 0, 0];
    if (pointRequired === FineTunedPointRequired.START_POINT) {
      tangentPose = PoseUtil.calculatePoseOnArc(
        startPose,
        midPose,
        endPose,
        arcDistance
      );
    } else if (pointRequired === FineTunedPointRequired.MID_POINT) {
      tangentPose = PoseUtil.calculatePoseOnArc(
        midPose,
        endPose,
        startPose,
        arcDistance
      );
    } else {
      tangentPose = PoseUtil.calculatePoseOnArc(
        endPose,
        midPose,
        startPose,
        arcDistance
      );
    }
    return tangentPose;
  }

  /**
   * Calculate an interpolated pose on the radial curve
   * created by 3 poses
   * Returns an interpolated pose on arc
   */
  public static interpolatePoseOnArc(
    startPose: Pose,
    midPose: Pose,
    endPose: Pose,
    pointRequired: FineTunedPointRequired,
    arcDistance: number
  ): Pose {
    // define constants
    const REVERSE_DIRECTION = 1;
    let tangentPose: Pose = [0, 0, 0, 0, 0, 0];
    if (pointRequired === FineTunedPointRequired.START_POINT) {
      tangentPose = PoseUtil.calculateInterpolatedPoseOnArc(
        startPose,
        midPose,
        endPose,
        arcDistance
      );
    } else if (pointRequired === FineTunedPointRequired.MID_POINT) {
      tangentPose = PoseUtil.calculateInterpolatedPoseOnArc(
        midPose,
        endPose,
        startPose,
        arcDistance
      );
    } else {
      tangentPose = PoseUtil.calculateInterpolatedPoseOnArc(
        endPose,
        midPose,
        startPose,
        arcDistance,
        REVERSE_DIRECTION
      );
    }
    return tangentPose;
  }

  // Infer Work piece Position from Waypoints
  /**
   * Infer the welding position from two poses.
   * @param {Pose} p1 - The first pose.
   * @param {Pose} p2 - The second pose.
   * @returns {WorkPiecePosition} - The inferred welding position:
   * FLAT, HORIZONTAL, VERTICAL, or OVERHEAD.
   */
  public static inferWorkPiecePosition(p1: Pose, p2: Pose): WorkPiecePosition {
    const ANGLE_THRESHOLD_FLAT = 15;
    const ANGLE_THRESHOLD_OVERHEAD = 130;
    const VERTICAL_POSITION_THRESHOLD = 0.8;

    const zAngle1 = this.getAngleOfPoseToGlobalFrame(p1, CoordinateName.Z_AXIS);
    const zDistance = Math.abs(p2[2] - p1[2]);
    const distanceThreshold =
      PoseUtil.point_dist(p1, p2) * VERTICAL_POSITION_THRESHOLD;
    if (zAngle1 <= ANGLE_THRESHOLD_FLAT) {
      return WorkPiecePosition.FLAT;
    } else if (zDistance < distanceThreshold) {
      return WorkPiecePosition.HORIZONTAL;
    } else if (zDistance >= distanceThreshold) {
      return WorkPiecePosition.VERTICAL;
    } else if (zAngle1 > ANGLE_THRESHOLD_OVERHEAD) {
      return WorkPiecePosition.OVERHEAD;
    } else {
      return WorkPiecePosition.HORIZONTAL;
      // Default to horizontal if no other condition is met
    }
  }

  /**
   * Calculate the angle between the Z axis vector of the input pose and the
   *  global base frame Zaxis vector
   */
  private static getAngleOfPoseToGlobalFrame(
    p1: Pose,
    vectorType = CoordinateName.Z_AXIS
  ): number {
    // Define constants
    const BASE_Z_AXIS_OFFSET = 180;

    // Z-axis Vector of Global Frame (base frame)
    const unit_Z_Vector = new Vector3(0, 0, 1);

    // Extract the Z vector from the Pose
    const axisVector = PoseUtil.extractVectorFromPose(p1, vectorType);
    // Calculate the angle between two vectors
    const angle = PoseUtil.calculateAngleBetweenVectors(
      axisVector,
      unit_Z_Vector
    );

    // Convert radians to degrees
    const angle_degrees = angle * RAD_TO_DEG;

    return BASE_Z_AXIS_OFFSET - angle_degrees;
  }

  private static invertData(data: FineTuneSettings): FineTuneSettings {
    return {
      alongLineOfTravel: -data.alongLineOfTravel,
      squareToLine: -data.squareToLine,
      alongTool: -data.alongTool,
      workAngle: -data.workAngle,
      travelAngle: -data.travelAngle,
      axisAngle: -data.axisAngle,
      // Add other properties if necessary
    };
  }

  /**
   * Generate Stitch points
   * returns a list of stitch segments
   * @param MotionPoint
   * @param StitchSettings
   * @return PoseList
   */
  public static generateStitchSegments(
    inputPoints: MotionPoint[],
    stitchProperties: StitchSettings
  ): MotionPoint[] {
    // Define Constants
    let segmentList: MotionPoint[] = [];
    const { lengthOfStitch, pitchOfStitch } = stitchProperties;
    // Total sequence length
    const totalSegmentLength = this.calculateTotalSegmentLength(inputPoints);
    const distanceBetweenSegments = round(pitchOfStitch - lengthOfStitch, 5);
    // Number of segments expected
    const totalSegments = this.getNumberOfStitchSegments(totalSegmentLength, {
      lengthOfStitch: lengthOfStitch,
      pitchOfStitch: pitchOfStitch,
    });
    let segmentCount = 0;

    // Push up first original point
    segmentList.push(inputPoints[0]);
    let current_segment: 'stitch' | 'air' = 'stitch';
    let current_distance = 0.0;
    for (let i = 0; i < inputPoints.length - 1; i++) {
      const currentWaypoint = inputPoints[i];
      const nextWaypoint = inputPoints[i + 1];
      const prevWaypoint = i > 0 ? inputPoints[i - 1] : { ...inputPoints[0] };
      const isLastItem = i + 1 >= inputPoints.length - 1;
      const afterNextItem = isLastItem ? undefined : inputPoints[i + 2];
      // calculate current segment length
      const segment_distance = this.calculateSegmentLength(
        currentWaypoint,
        nextWaypoint,
        prevWaypoint,
        afterNextItem,
        isLastItem
      );
      current_distance = current_distance + segment_distance;

      // Push next point if it lies within segment
      if (
        current_segment === 'stitch' &&
        round(current_distance, 4) < this.toleranceOf(lengthOfStitch) &&
        segmentCount < totalSegments
      ) {
        console.log('push next wp', i + 1);
        segmentList.push(nextWaypoint);
      }
      // Generate new points and update Output Segment List
      while (
        round(current_distance, 4) >=
          (current_segment === 'stitch'
            ? this.toleranceOf(lengthOfStitch)
            : this.toleranceOf(distanceBetweenSegments)) &&
        segmentCount < totalSegments
      ) {
        const required_dist =
          current_segment === 'stitch'
            ? lengthOfStitch
            : distanceBetweenSegments;

        segmentList = this.calculateStitchSegments(
          i,
          inputPoints,
          segmentList,
          segment_distance,
          current_distance,
          required_dist,
          current_segment
        );
        current_distance = round(current_distance - required_dist, 5);
        console.log('remaning dist ', current_distance);

        // Switch segment type
        if (current_segment === 'stitch') {
          current_segment = 'air';
          // Add air move
          const airWaypoint = this.createAirPoint(
            segmentList[segmentList.length - 1]
          );
          segmentList.push(airWaypoint);

          segmentCount = segmentCount + 1;
          console.log('switching to air');
        } else if (current_segment === 'air') {
          current_segment = 'stitch';
          console.log('switching to stitch segment');

          // Add more points for validation if needed
          if (current_distance < this.toleranceOf(lengthOfStitch, 5)) {
            const outputListLastIndex = segmentList.length - 1;
            if (
              segmentList[outputListLastIndex].type === MotionType.RADIAL &&
              nextWaypoint.type !== MotionType.RADIAL
            ) {
              // Add extra radial point
              const exrtaWaypoint = this.createRadialPoint(
                segmentList[outputListLastIndex - 2],
                segmentList[outputListLastIndex],
                nextWaypoint,
                ArcSegment.SECOND_SEGMENT
              );
              console.log('adding extra radial point');
              segmentList.push(exrtaWaypoint);
            } else if (nextWaypoint.type === MotionType.RADIAL && !isLastItem) {
              if (
                PoseUtil.pose_dist(
                  segmentList[outputListLastIndex].pose,
                  nextWaypoint.pose
                ) > 0.002
              ) {
                console.log('push next wp ', i + 1);
                segmentList.push(nextWaypoint);
              }
            }
          }
        }
      }
    }
    return segmentList;
  }

  private static toleranceOf(val1: number, precision = 4): number {
    return round(val1 * 0.99, precision);
  }

  public static calculateStitchSegments(
    listIndex: number,
    originalList: MotionPoint[],
    outputList: MotionPoint[],
    segmentDist: number,
    currentDist: number,
    requiredDist: number,
    segment = 'stitch'
  ): MotionPoint[] {
    // Define Constants
    const POSE_DIST_THRESHOLD = 0.004; // Threshold to avoid overlapping points being added
    const currentPoint = originalList[listIndex];
    const nextPoint = originalList[listIndex + 1];
    const prevPoint = listIndex > 0 ? originalList[listIndex - 1] : undefined;
    const isLastPoint = listIndex + 1 >= originalList.length - 1;

    const ratio = this.getInterpolateRatio(
      segmentDist,
      currentDist,
      requiredDist
    );

    const newWaypoint = this.findNextSegmentPoint(
      currentPoint,
      nextPoint,
      prevPoint,
      originalList[listIndex + 2],
      isLastPoint,
      ratio
    );
    // Add additional points: Mid points for radial
    if (segment === 'stitch') {
      const lastIndex = outputList.length - 1;
      let prevOutputPoint: MotionPoint = { ...outputList[lastIndex] };
      if (
        currentPoint.type === MotionType.RADIAL &&
        newWaypoint.type === MotionType.RADIAL &&
        prevOutputPoint.type === MotionType.RADIAL
      ) {
        if (
          PoseUtil.pose_dist(prevOutputPoint.pose, newWaypoint.pose) <
          POSE_DIST_THRESHOLD
        ) {
          outputList.pop();
          prevOutputPoint = { ...outputList[outputList.length - 1] };
        }
        let [point1, point2, point3] = [
          prevOutputPoint,
          newWaypoint,
          nextPoint,
        ];
        let requiredArcTheta = ArcSegment.FIRST_SEGMENT;

        const shouldUseOtherPoint =
          PoseUtil.pose_dist(newWaypoint.pose, nextPoint.pose) <
          POSE_DIST_THRESHOLD;

        if (shouldUseOtherPoint) {
          console.log('should use diff points for midpoint calc');
          if (!isLastPoint) {
            [point1, point2, point3] = [
              prevOutputPoint,
              nextPoint,
              originalList[listIndex + 2],
            ];
          } else {
            [point1, point2, point3] =
              segmentDist < requiredDist
                ? [prevOutputPoint, currentPoint, newWaypoint]
                : [currentPoint, prevOutputPoint, newWaypoint];
            requiredArcTheta = ArcSegment.SECOND_SEGMENT;
          }
        }
        const midPoint = this.createRadialPoint(
          point1,
          point2,
          point3,
          requiredArcTheta
        );
        if (
          lastIndex === 0 ||
          (lastIndex > 0 &&
            outputList[outputList.length - 2].type != MotionType.RADIAL &&
            outputList[outputList.length - 1].type === MotionType.RADIAL)
        ) {
          outputList.push(midPoint);
        }
      } else if (
        currentPoint.type === MotionType.LINEAR &&
        round(PoseUtil.point_dist(currentPoint.pose, nextPoint.pose), 5) <
          requiredDist &&
        round(PoseUtil.point_dist(prevOutputPoint.pose, currentPoint.pose), 5) >
          0.001
      ) {
        outputList.push(currentPoint);
      }
    }
    outputList.push(newWaypoint);
    return outputList;
  }

  public static findNextSegmentPoint(
    currentPoint: MotionPoint,
    nextPoint: MotionPoint,
    previousPoint: MotionPoint | undefined,
    pointAfterNextPoint: MotionPoint | undefined,
    isNextPointLastItem: boolean,
    distanceRatio: number
  ): MotionPoint {
    let newWaypoint: MotionPoint = { ...currentPoint };
    if (currentPoint.type === MotionType.LINEAR) {
      newWaypoint.pose = PoseUtil.linear_interpolate_pose(
        currentPoint.pose,
        nextPoint.pose,
        distanceRatio
      );
    } else if (currentPoint.type === MotionType.RADIAL) {
      if (
        nextPoint.type === MotionType.RADIAL &&
        !isNextPointLastItem &&
        pointAfterNextPoint
      ) {
        newWaypoint = this.createRadialPoint(
          currentPoint,
          nextPoint,
          pointAfterNextPoint,
          ArcSegment.FIRST_SEGMENT,
          distanceRatio
        );
      } else if (
        (nextPoint.type !== MotionType.RADIAL || isNextPointLastItem) &&
        previousPoint?.type === MotionType.RADIAL
      ) {
        newWaypoint = this.createRadialPoint(
          previousPoint,
          currentPoint,
          nextPoint,
          ArcSegment.SECOND_SEGMENT,
          distanceRatio
        );
      }
    }
    return newWaypoint;
  }

  public static createAirPoint(point1: MotionPoint): MotionPoint {
    // Constants
    const DEPART_AIR_POSE: Pose = [0, 0, -0.035, 0, 0, 0];

    const airWaypoint: MotionPoint = {
      pose: PoseUtil.pose_trans(point1.pose, DEPART_AIR_POSE),
      qnear: point1.qnear,
      type: MotionType.AIR,
    };
    return airWaypoint;
  }

  public static createRadialPoint(
    point1: MotionPoint,
    point2: MotionPoint,
    point3: MotionPoint,
    segmentRequired = ArcSegment.FIRST_SEGMENT,
    ratio = 0.5
  ): MotionPoint {
    // find segment length
    const arcSegmentLength = this.calculateTotalSegmentLength([
      point1,
      point2,
      point3,
    ]);
    // calculate thetaA2B or thetaB2C
    const theta = this.calculateRadialDist(
      point1.pose,
      point2.pose,
      point3.pose,
      segmentRequired
    );
    // Interpolation ratio
    const interpolateRatio = ratio * (theta / arcSegmentLength);

    // New Pose on arc
    let positionOnArc = PoseUtil.getPoseOnArcAtRadian(
      point1.pose,
      point2.pose,
      point3.pose,
      interpolateRatio
    );
    let axisAngleOnArc = PoseUtil.interpolateQuaternion(
      point1.pose,
      point2.pose,
      ratio
    );

    if (segmentRequired === ArcSegment.SECOND_SEGMENT) {
      const newRatio = round(
        interpolateRatio + (arcSegmentLength - theta) / arcSegmentLength,
        3
      );
      positionOnArc = PoseUtil.getPoseOnArcAtRadian(
        point1.pose,
        point2.pose,
        point3.pose,
        newRatio
      );
      axisAngleOnArc = PoseUtil.interpolateQuaternion(
        point2.pose,
        point3.pose,
        ratio
      );
    }
    // interpolated pose on arc
    const interpolatedPose: Pose = [
      positionOnArc[0],
      positionOnArc[1],
      positionOnArc[2],
      axisAngleOnArc.x,
      axisAngleOnArc.y,
      axisAngleOnArc.z,
    ];
    const newPoint: MotionPoint = {
      pose: interpolatedPose,
      type: MotionType.RADIAL,
      qnear: point2.qnear,
    };

    return newPoint;
  }

  private static getInterpolateRatio(
    segmentDistance: number,
    currentDistance: number,
    requiredDistance: number
  ): number {
    const remaining_dist =
      requiredDistance - (currentDistance - segmentDistance);
    const alphaValue = remaining_dist / segmentDistance;
    return alphaValue;
  }

  public static getNumberOfStitchSegments(
    totalSegmentLength: number,
    stitchSettings: StitchSettings
  ): number {
    const { lengthOfStitch, pitchOfStitch } = stitchSettings;
    const adjustedSegmentLength = totalSegmentLength - lengthOfStitch;
    const initialNumOfSegments = round(
      adjustedSegmentLength / pitchOfStitch,
      1
    );
    const fullSegments = floor(initialNumOfSegments + 1);
    console.log('Expected full segments = ', fullSegments);
    return fullSegments;
  }

  // Modification of User Stitch inputs
  public static validateStitchSettings(
    inputPoints: MotionPoint[],
    initialSettings: StitchSettings,
    options: StitchFitOptions
  ): ValidateStitchSettingsResult {
    const totalSegmentLength = this.calculateTotalSegmentLength(inputPoints);
    let newStitchSettings: StitchSettings = { ...initialSettings };
    if (options.lockStitchLength && !options.lockPitch) {
      newStitchSettings = {
        lengthOfStitch: initialSettings.lengthOfStitch,
        pitchOfStitch: this.getAdjustedPitch(
          totalSegmentLength,
          initialSettings
        ),
      };
    } else if (options.lockPitch && !options.lockStitchLength) {
      newStitchSettings = {
        lengthOfStitch: this.adjustStitchLength(
          totalSegmentLength,
          initialSettings
        ),
        pitchOfStitch: initialSettings.pitchOfStitch,
      };
    }
    return {
      modifiedSettings: newStitchSettings,
      totalSequenceLength: totalSegmentLength,
    };
  }

  public static getAdjustedPitch(
    totalLength: number,
    stitchSettings: StitchSettings
  ): number {
    const { lengthOfStitch, pitchOfStitch } = stitchSettings;
    const adjustedSegmentLength = round(totalLength - lengthOfStitch, 5);
    const initialNumOfSegments = adjustedSegmentLength / pitchOfStitch;
    const numOfFullSegments = round(initialNumOfSegments);
    const newPitchLength = round(adjustedSegmentLength / numOfFullSegments, 4);
    return newPitchLength;
  }

  public static adjustStitchLength(
    totalLength: number,
    stitchSettings: StitchSettings
  ): number {
    const { lengthOfStitch, pitchOfStitch } = stitchSettings;

    const numOfSegments = this.getNumberOfStitchSegments(
      totalLength,
      stitchSettings
    );
    const totalStitchLen = numOfSegments * lengthOfStitch;
    const totalNonStitchLen =
      (numOfSegments - 1) * (pitchOfStitch - lengthOfStitch);
    const remainingLength = totalLength - (totalStitchLen + totalNonStitchLen);
    const adjustedStitchLen = round(
      lengthOfStitch + remainingLength / numOfSegments,
      4
    );
    return adjustedStitchLen;
  }

  public static calculateTotalSegmentLength(
    inputPoints: MotionPoint[]
  ): number {
    // Define Constants
    let segmentDistance = 0;

    for (let i = 0; i < inputPoints.length - 1; i++) {
      const thisItem = inputPoints[i];
      const nextItemIndex = i + 1;
      const isNextLastItem = nextItemIndex >= inputPoints.length - 1;
      const nextItem = inputPoints[nextItemIndex];
      const prevItem = i > 0 ? inputPoints[i - 1] : undefined;
      const afterNextItem = isNextLastItem ? undefined : inputPoints[i + 2];

      segmentDistance += this.calculateSegmentLength(
        thisItem,
        nextItem,
        prevItem,
        afterNextItem,
        isNextLastItem
      );
    }
    return round(segmentDistance, 5);
  }

  public static calculateSegmentLength(
    currentPoint: MotionPoint,
    nextPoint: MotionPoint,
    prevPoint: MotionPoint | undefined,
    nextPlusOnePoint: MotionPoint | undefined,
    isNextLastSegment = false
  ): number {
    let segmentDistance = 0;
    if (currentPoint.type === MotionType.LINEAR) {
      // calculate linear distance to next point
      segmentDistance = this.calculateLinearDist(
        currentPoint.pose,
        nextPoint.pose
      );
    } else if (currentPoint.type === MotionType.RADIAL) {
      if (
        nextPoint.type === MotionType.RADIAL &&
        nextPlusOnePoint != undefined
      ) {
        segmentDistance = this.calculateRadialDist(
          currentPoint.pose,
          nextPoint.pose,
          nextPlusOnePoint?.pose,
          ArcSegment.FIRST_SEGMENT
        );
      } else if (
        prevPoint?.type === MotionType.RADIAL &&
        (isNextLastSegment || nextPoint.type != MotionType.RADIAL)
      ) {
        segmentDistance = this.calculateRadialDist(
          prevPoint?.pose,
          currentPoint.pose,
          nextPoint.pose,
          ArcSegment.SECOND_SEGMENT
        );
      }
    }
    return isNaN(segmentDistance) ? 0 : round(segmentDistance, 5);
  }

  private static calculateLinearDist(pose1: Pose, pose2: Pose): number {
    return PoseUtil.point_dist(pose1, pose2);
  }

  public static calculateRadialDist(
    pose1: Pose,
    pose2: Pose,
    pose3: Pose,
    segmentRequired = ArcSegment.FIRST_SEGMENT
  ): number {
    // Define Constants
    const centerPose = PoseUtil.findCenterOfArc(pose1, pose2, pose3);
    const radiusOfArc = PoseUtil.findRadiusOfArc(pose1, pose2, pose3);
    const segment_distance_1 = PoseUtil.findArcRadians(
      pose1,
      pose2,
      centerPose
    );
    const segment_distance_2 = PoseUtil.findArcRadians(
      pose2,
      pose3,
      centerPose
    );

    const segmentLength_radians =
      segmentRequired === ArcSegment.FIRST_SEGMENT
        ? segment_distance_1
        : segment_distance_2;

    const segmentLength = segmentLength_radians * radiusOfArc;

    return segmentLength;
  }

  public static calculateTotalArcRadians(
    inputPoses: Array<Pose>,
    centerPose: Pose
  ): number {
    let totalArcRadians = 0;

    for (let i = 0; i < inputPoses.length - 2; i++) {
      const segment_radians = PoseUtil.findArcRadians(
        inputPoses[i],
        inputPoses[i + 1],
        centerPose
      );
      totalArcRadians += segment_radians;
    }

    return totalArcRadians;
  }

  /* MULTIPASS PATH FOR WELDING
  ## Convert a root pass into multipass paths for welding
  ## Generate new points according to offset settings
  */
  public static generateMultiPass(
    inputPointsArray: MotionPoint[],
    multiPassSettings: MultiPassSettings[]
  ): MotionPoint[] {
    // Define Constants
    let outputPoints: MotionPoint[] = [];
    const numOfPasses = multiPassSettings.length;

    // Generate multipass points
    for (let i = 0; i < numOfPasses; i++) {
      const currentPassOffsets = multiPassSettings[i].passOffsets;
      const currentPassPosition = multiPassSettings[i].partPosition;
      const currentPassTravelDir = multiPassSettings[i].passDirection;
      const currentPassPoints = this.applyPointOffsets(
        inputPointsArray,
        currentPassOffsets,
        currentPassTravelDir,
        currentPassPosition
      );

      // Add depart moves between after every pass
      const departAirPoint = this.createAirPoint(
        currentPassPoints[currentPassPoints.length - 1]
      );
      // // Methods to add clearance points to retrace motion
      // const nextPassTravelDir =
      //   i < numOfPasses - 1
      //     ? multiPassSettings[i + 1].passDirection
      //     : 'forward';
      // const clearancePoints = this.addClearancePoints(
      //   currentPassPoints,
      //   currentPassTravelDir,
      //   nextPassTravelDir
      // );

      // Add points to output list
      outputPoints = outputPoints.concat(currentPassPoints);
      outputPoints = outputPoints.concat(departAirPoint);
    }
    // console.log('output points = ', outputPoints);
    return outputPoints;
  }

  public static applyPointOffsets(
    inputPoints: MotionPoint[],
    offsetList: MultipassOffsets,
    travelDirection: 'forward' | 'reverse',
    partPosition?: WorkPiecePosition
  ): MotionPoint[] {
    // Reverse direction of torch offset
    offsetList.alongTorchWire = -offsetList.alongTorchWire;
    // Reverse points if needed
    if (travelDirection === 'reverse') {
      inputPoints = this.reverseOrderOfPoints(inputPoints);
      offsetList.startAlongPath = -offsetList.startAlongPath;
      offsetList.endAlongPath = -offsetList.endAlongPath;
    }
    const modifiedRotationsOfPoses = this.applyTorchRotations(
      inputPoints,
      offsetList,
      travelDirection,
      partPosition
    );

    const outputPoints = this.applyTorchTranslations(
      modifiedRotationsOfPoses,
      offsetList,
      travelDirection,
      partPosition
    );
    // Return offset points
    return outputPoints;
  }

  public static calculateTorchAngles(
    inputPoints: MotionPoint[],
    partPosition?: WorkPiecePosition,
    mode?: AngleModificationMode
  ): FineTuneAngles[] {
    const rootTorchAngles: FineTuneAngles[] = [];
    for (let i = 0; i < inputPoints.length; i++) {
      const currentPoint = inputPoints[i];
      const nextPoint =
        i < inputPoints.length - 1 ? inputPoints[i + 1] : undefined;
      const previousPoint = i > 0 ? inputPoints[i - 1] : undefined;
      const isLastItem = i === inputPoints.length - 1;

      if (currentPoint.type === MotionType.LINEAR) {
        if (nextPoint !== undefined) {
          let poinToUse = FineTunedPointRequired.START_POINT;
          let nextMove = nextPoint.pose;
          if (
            this.shouldModifyAngleCalculation(
              currentPoint.pose,
              nextPoint.pose
            ) &&
            previousPoint?.type !== undefined
          ) {
            poinToUse = FineTunedPointRequired.END_POINT;
            nextMove = previousPoint.pose;
          }
          rootTorchAngles.push(
            this.getCurrentTorchAngles_linear(
              currentPoint.pose,
              nextMove,
              poinToUse,
              previousPoint?.pose,
              partPosition,
              undefined,
              mode
            )
          );
        } else if (previousPoint?.type == MotionType.LINEAR && isLastItem) {
          rootTorchAngles.push(
            this.getCurrentTorchAngles_linear(
              currentPoint.pose,
              previousPoint.pose,
              FineTunedPointRequired.END_POINT,
              previousPoint.pose,
              partPosition,
              undefined,
              mode
            )
          );
        } else {
          rootTorchAngles.push({ travelAngle: 0, workAngle: 0 });
        }
      } else if (currentPoint.type === MotionType.RADIAL) {
        if (
          previousPoint === undefined ||
          previousPoint?.type !== MotionType.RADIAL
        ) {
          const nextPlusOnePoint = inputPoints[i + 2];

          rootTorchAngles.push(
            this.getCurrentTorchAngles_radial(
              currentPoint.pose,
              nextPoint!.pose,
              nextPlusOnePoint.pose,
              FineTunedPointRequired.START_POINT
            )
          );
        } else if (
          previousPoint?.type === MotionType.RADIAL &&
          nextPoint !== undefined
        ) {
          rootTorchAngles.push(
            this.getCurrentTorchAngles_radial(
              previousPoint.pose,
              currentPoint.pose,
              nextPoint!.pose,
              FineTunedPointRequired.MID_POINT
            )
          );
        } else if (isLastItem && nextPoint == undefined) {
          const previousMinus = inputPoints[i - 2];
          rootTorchAngles.push(
            this.getCurrentTorchAngles_radial(
              previousMinus!.pose,
              previousPoint.pose,
              currentPoint.pose,
              FineTunedPointRequired.END_POINT
            )
          );
        }
      } else {
        const ghostPoint = PoseUtil.pose_trans(
          currentPoint.pose,
          [0.005, 0, 0, 0, 0, 0]
        );
        rootTorchAngles.push(
          this.getCurrentTorchAngles_linear(
            currentPoint.pose,
            ghostPoint,
            FineTunedPointRequired.START_POINT
          )
        );
      }
    }
    return rootTorchAngles;
  }

  public static applyTorchRotations(
    inputPoints: MotionPoint[],
    offsetList: MultipassOffsets,
    travelDirection: 'forward' | 'reverse',
    partPosition?: WorkPiecePosition
  ): MotionPoint[] {
    const outputPoints: MotionPoint[] = [];
    // calculate torch angles of all input points
    const inputPointsTorchAngles = this.calculateTorchAngles(
      inputPoints,
      partPosition,
      offsetList.mode
    );

    for (let i = 0; i < inputPoints.length; i++) {
      // Define points required
      const prevPose = i > 0 ? inputPoints[i - 1].pose : undefined;
      const currentPoint = inputPoints[i];
      const previousPoint = i > 0 ? inputPoints[i - 1] : undefined;
      const nextPoint =
        i < inputPoints.length - 1 ? inputPoints[i + 1] : undefined;
      const isLastItem = i === inputPoints.length - 1;

      // Define required torch angles
      const currentPointTorchAngles = inputPointsTorchAngles[i];
      const isRelative = offsetList.mode === AngleModificationMode.RELATIVE;

      let adjustForTravelDirection = 0;
      if (travelDirection === 'reverse') {
        const requiredTravelAngle = -(currentPointTorchAngles.travelAngle ?? 0);
        adjustForTravelDirection =
          requiredTravelAngle - (currentPointTorchAngles.travelAngle ?? 0);
      }

      const desiredTorchAngles: FineTuneAngles = {
        travelAngle: isRelative
          ? offsetList.travelAngle + adjustForTravelDirection
          : offsetList.travelAngle - (currentPointTorchAngles.travelAngle ?? 0),
        workAngle: isRelative
          ? offsetList.workAngle
          : offsetList.workAngle - (currentPointTorchAngles.workAngle ?? 0),
      };
      // Apply torch angle offsets
      let offsetPoint: MotionPoint = { ...currentPoint };
      if (currentPoint.type === MotionType.LINEAR) {
        if (nextPoint !== undefined) {
          offsetPoint = {
            ...currentPoint,
            pose: this.setTorchAngleOffsets_linear(
              currentPoint.pose,
              nextPoint.pose,
              FineTunedPointRequired.START_POINT,
              desiredTorchAngles.travelAngle,
              desiredTorchAngles.workAngle,
              partPosition,
              prevPose,
              offsetList.mode,
              travelDirection
            ),
          };
        } else if (previousPoint?.type == MotionType.LINEAR && isLastItem) {
          offsetPoint = {
            ...currentPoint,
            pose: this.setTorchAngleOffsets_linear(
              currentPoint.pose, // translated pose
              previousPoint.pose,
              FineTunedPointRequired.END_POINT,
              desiredTorchAngles.travelAngle,
              desiredTorchAngles.workAngle,
              partPosition,
              prevPose,
              offsetList.mode,
              travelDirection
            ),
          };
        }
        // Conditions for Radial point type
      } else if (currentPoint.type === MotionType.RADIAL) {
        if (
          previousPoint === undefined ||
          previousPoint?.type !== MotionType.RADIAL
        ) {
          const nextPlusOnePoint = inputPoints[i + 2];
          offsetPoint = {
            ...currentPoint,
            pose: this.setTorchAngleOffsets_radial(
              currentPoint.pose,
              nextPoint!.pose,
              nextPlusOnePoint.pose,
              FineTunedPointRequired.START_POINT,
              desiredTorchAngles.travelAngle,
              desiredTorchAngles.workAngle,
              partPosition
            ),
          };
        } else if (
          previousPoint?.type === MotionType.RADIAL &&
          nextPoint !== undefined
        ) {
          offsetPoint = {
            ...currentPoint,
            pose: this.setTorchAngleOffsets_radial(
              previousPoint.pose,
              currentPoint.pose,
              nextPoint!.pose,
              FineTunedPointRequired.MID_POINT,
              desiredTorchAngles.travelAngle,
              desiredTorchAngles.workAngle,
              partPosition
            ),
          };
        } else if (isLastItem && nextPoint == undefined) {
          const previousMinus = inputPoints[i - 2];

          offsetPoint = {
            ...currentPoint,
            pose: this.setTorchAngleOffsets_radial(
              previousMinus!.pose,
              previousPoint.pose,
              currentPoint.pose,
              FineTunedPointRequired.END_POINT,
              desiredTorchAngles.travelAngle,
              desiredTorchAngles.workAngle,
              partPosition
            ),
          };
        }
      } else {
        const ghostPoint = PoseUtil.pose_trans(
          currentPoint.pose,
          [0.005, 0, 0, 0, 0, 0]
        );
        offsetPoint = {
          ...currentPoint,
          pose: this.setTorchAngleOffsets_linear(
            currentPoint.pose,
            ghostPoint,
            FineTunedPointRequired.START_POINT,
            desiredTorchAngles.travelAngle,
            desiredTorchAngles.workAngle,
            partPosition
          ),
        };
      }
      outputPoints.push(offsetPoint);
    }
    return outputPoints;
  }

  public static applyTorchTranslations(
    inputPoints: MotionPoint[],
    offsetList: MultipassOffsets,
    travelDirection: 'forward' | 'reverse',
    partPosition?: WorkPiecePosition
  ): MotionPoint[] {
    const outputPoints: MotionPoint[] = [];
    const reverseOffset =
      offsetList.mode === AngleModificationMode.RELATIVE &&
      travelDirection === 'reverse';
    for (let i = 0; i < inputPoints.length; i++) {
      // Define Constants
      const currentPoint = inputPoints[i];
      const previousPoint = i > 0 ? inputPoints[i - 1] : undefined;
      const nextPoint =
        i < inputPoints.length - 1 ? inputPoints[i + 1] : undefined;
      const isLastItem = i === inputPoints.length - 1;

      const pathOffset: FineTuneSettings = {
        alongLineOfTravel:
          i === 0
            ? offsetList.startAlongPath
            : isLastItem
              ? offsetList.endAlongPath
              : 0,
        squareToLine: offsetList.lateralToPath,
        alongTool: 0,
        workAngle: 0,
        travelAngle: 0,
        axisAngle: 0,
      };

      let offsetPoint: MotionPoint = { ...currentPoint };
      if (currentPoint.type === MotionType.LINEAR) {
        if (nextPoint !== undefined) {
          let pointToModify = FineTunedPointRequired.START_POINT;
          let pointToMove = nextPoint.pose;
          if (
            this.shouldModifyAngleCalculation(
              currentPoint.pose,
              nextPoint.pose
            ) &&
            previousPoint?.pose !== undefined
          ) {
            pointToMove = previousPoint.pose;
            pointToModify = FineTunedPointRequired.END_POINT;
          }

          offsetPoint = {
            ...currentPoint,
            pose: this.getFineTunedPoseToPath_linear(
              currentPoint.pose,
              pointToMove,
              pathOffset,
              pointToModify,
              partPosition,
              reverseOffset
            ),
          };
        } else if (previousPoint?.type == MotionType.LINEAR && isLastItem) {
          offsetPoint = {
            ...currentPoint,
            pose: this.getFineTunedPoseToPath_linear(
              currentPoint.pose, // current pose
              previousPoint.pose, // previous pose
              pathOffset, // path offset settings
              FineTunedPointRequired.END_POINT, // we are at the end of the path
              partPosition, // workpiece position
              reverseOffset
            ),
          };
        }
        // Conditions for Radial point type
      } else if (currentPoint.type === MotionType.RADIAL) {
        if (
          previousPoint === undefined ||
          previousPoint?.type !== MotionType.RADIAL
        ) {
          const nextPlusOnePoint = inputPoints[i + 2];
          offsetPoint = {
            ...currentPoint,
            pose: this.getFineTunedPoseToPath_radial(
              currentPoint.pose,
              nextPoint!.pose,
              nextPlusOnePoint.pose,
              pathOffset,
              FineTunedPointRequired.START_POINT,
              partPosition
            ),
          };
        } else if (
          previousPoint?.type === MotionType.RADIAL &&
          nextPoint !== undefined
        ) {
          offsetPoint = {
            ...currentPoint,
            pose: this.getFineTunedPoseToPath_radial(
              previousPoint.pose,
              currentPoint.pose,
              nextPoint!.pose,
              pathOffset,
              FineTunedPointRequired.MID_POINT,
              partPosition
            ),
          };
        } else if (isLastItem && nextPoint == undefined) {
          const previousMinus = inputPoints[i - 2];
          offsetPoint = {
            ...currentPoint,
            pose: this.getFineTunedPoseToPath_radial(
              previousMinus!.pose,
              previousPoint.pose,
              currentPoint.pose,
              pathOffset,
              FineTunedPointRequired.END_POINT,
              partPosition
            ),
          };
        }
      } else {
        const ghostPoint = PoseUtil.pose_trans(
          currentPoint.pose,
          [0.005, 0, 0, 0, 0, 0]
        );
        offsetPoint = {
          ...currentPoint,
          pose: this.getFineTunedPoseToPath_linear(
            currentPoint.pose,
            ghostPoint,
            pathOffset,
            FineTunedPointRequired.START_POINT
          ),
        };
      }
      offsetPoint.pose = PoseUtil.pose_trans(offsetPoint.pose, [
        0,
        0,
        offsetList.alongTorchWire / 1000,
        0,
        0,
        0,
      ]);
      outputPoints.push(offsetPoint);
    }
    return outputPoints;
  }

  public static addClearancePoints(
    inputPoints: MotionPoint[],
    currentDirection: string,
    nextDirection: string
  ): MotionPoint[] {
    if (
      (currentDirection === 'forward' && nextDirection === 'reverse') ||
      (currentDirection === 'reverse' && nextDirection === 'forward')
    ) {
      const lastAirPoint = this.convertToAirPoints(
        [inputPoints[inputPoints.length - 1]],
        currentDirection
      );
      return lastAirPoint; // Convert the last item to an air point
    }

    const airPoints = this.convertToAirPoints(inputPoints, 'reverse');

    if (
      (currentDirection === 'forward' && nextDirection === 'forward') ||
      (currentDirection === 'reverse' && nextDirection === 'reverse')
    ) {
      return airPoints; // Add clearance retrace moves
    }

    return airPoints;
  }

  public static convertToAirPoints(
    inputPoints: MotionPoint[],
    direction = 'forward'
  ): MotionPoint[] {
    const outputPoints: MotionPoint[] = inputPoints.map((point) =>
      this.createAirPoint(point)
    );

    if (direction === 'reverse') {
      return outputPoints.reverse();
    }

    return outputPoints;
  }

  public static reverseOrderOfPoints(
    inputPoints: MotionPoint[]
  ): MotionPoint[] {
    return inputPoints.slice().reverse();
  }

  public static getTorchOffsetToPath(
    inputPoints: MotionPoint[],
    currentPose: Pose,
    angleMode: AngleModificationMode,
    passDirection = 'forward',
    partPosition?: WorkPiecePosition
  ): BeaconWeldingData.MultipassPointOffsetFormData {
    // If reverse, invert the input points
    if (passDirection === 'reverse') {
      inputPoints = this.reverseOrderOfPoints(inputPoints);
    }
    // Calculate the origin reference frame
    const refPose1 = inputPoints[0].pose;
    let refPose2 = inputPoints[1].pose;

    // Check if the first point is linear or radial
    if (inputPoints[0].type === MotionType.RADIAL) {
      const tangent_pose = this.calculateArcTangentPose(
        inputPoints[0].pose,
        inputPoints[1].pose,
        inputPoints[2].pose,
        FineTunedPointRequired.START_POINT
      );
      refPose2 = tangent_pose;
    } else if (inputPoints[0].type !== MotionType.LINEAR) {
      const ghostPoint = PoseUtil.pose_trans(
        inputPoints[0].pose,
        [0.005, 0, 0, 0, 0, 0]
      );
      refPose2 = ghostPoint;
    }
    if (angleMode === AngleModificationMode.RELATIVE) {
      partPosition = this.inferWorkPiecePosition(refPose1, refPose2);
    }
    // Calculate the offset pose
    const offsetToOrigin = this.calculateOffsetOfPosesToReferenceFrame(
      refPose1,
      refPose2,
      currentPose,
      partPosition
    );

    // Apply offset to the next point to get new weld path
    const offsetData: FineTuneSettings = {
      alongLineOfTravel: 0,
      squareToLine: offsetToOrigin[1],
      alongTool: 0,
      workAngle: 0,
      travelAngle: 0,
      axisAngle: 0,
    };
    let offsetPoseTorchAngles: FineTuneAngles = {
      travelAngle: 0,
      workAngle: 0,
    };
    let originalPoseTorchAngles: FineTuneAngles = {
      travelAngle: 0,
      workAngle: 0,
    };

    const addOffsetInZ = (pose: Pose): Pose => {
      return PoseUtil.pose_trans(pose, [
        0,
        0,
        offsetToOrigin[2] / 1000,
        0,
        0,
        0,
      ]);
    };

    // Check if the first point is linear or radial
    if (inputPoints[0].type === MotionType.RADIAL) {
      let midPose = this.getFineTunedPoseToPath_radial(
        currentPose,
        inputPoints[1].pose,
        inputPoints[2].pose,
        offsetData,
        FineTunedPointRequired.MID_POINT,
        partPosition
      );
      midPose = addOffsetInZ(midPose);
      let nextPose = this.getFineTunedPoseToPath_radial(
        currentPose,
        midPose,
        inputPoints[2].pose,
        offsetData,
        FineTunedPointRequired.END_POINT,
        partPosition
      );
      nextPose = addOffsetInZ(nextPose);
      originalPoseTorchAngles = this.getCurrentTorchAngles_radial(
        inputPoints[0].pose,
        inputPoints[1].pose,
        inputPoints[2].pose,
        FineTunedPointRequired.START_POINT,
        partPosition
      );
      offsetPoseTorchAngles = this.getCurrentTorchAngles_radial(
        currentPose,
        midPose,
        nextPose,
        FineTunedPointRequired.START_POINT,
        partPosition
      );
    } else {
      let nextPose = this.getFineTunedPoseToPath_linear(
        refPose2,
        currentPose,
        offsetData,
        FineTunedPointRequired.END_POINT,
        partPosition
      );
      nextPose = addOffsetInZ(nextPose);
      originalPoseTorchAngles = this.getCurrentTorchAngles_linear(
        refPose1,
        refPose2,
        FineTunedPointRequired.START_POINT,
        undefined,
        partPosition
      );
      offsetPoseTorchAngles = this.getCurrentTorchAngles_linear(
        currentPose,
        nextPose,
        FineTunedPointRequired.START_POINT,
        undefined,
        partPosition
      );
    }

    const torchOffset_fixedAngle: BeaconWeldingData.MultipassPointOffsetFormData =
      {
        startOffsetX: offsetToOrigin[0],
        endOffsetX: offsetToOrigin[0],
        perpendicularToPathY: offsetToOrigin[1],
        stickoutZ: -offsetToOrigin[2],
        travelAngle: offsetPoseTorchAngles.travelAngle ?? 0,
        workAngle: offsetPoseTorchAngles.workAngle ?? 0,
      };
    const torchOffset_relative: BeaconWeldingData.MultipassPointOffsetFormData =
      {
        startOffsetX: offsetToOrigin[0],
        endOffsetX: offsetToOrigin[0],
        perpendicularToPathY: offsetToOrigin[1],
        stickoutZ: -offsetToOrigin[2],
        travelAngle:
          (offsetPoseTorchAngles.travelAngle ?? 0) -
          (originalPoseTorchAngles.travelAngle ?? 0),
        workAngle:
          (offsetPoseTorchAngles.workAngle ?? 0) -
          (originalPoseTorchAngles.workAngle ?? 0),
      };

    if (angleMode === AngleModificationMode.RELATIVE) {
      return torchOffset_relative;
    } else {
      return torchOffset_fixedAngle;
    }
  }

  public static calculateOffsetOfPosesToReferenceFrame(
    pathPose1: Pose,
    pathPose2: Pose,
    newPose: Pose,
    partPosition?: WorkPiecePosition
  ): Pose {
    // Check if path is rotated
    let isPathRotated = PoseUtil.isLineRotated(pathPose1, pathPose2);
    if (partPosition === undefined) {
      partPosition = this.inferWorkPiecePosition(pathPose1, pathPose2);
    }
    if (partPosition == WorkPiecePosition.VERTICAL) {
      isPathRotated = true;
    }

    const originRefFrame = PoseUtil.getCustomFeaturePlaneOrigin(
      pathPose1,
      pathPose2
    );
    // Get start point in origin frame
    const startPointInOriginFrame = PoseUtil.pose_trans(
      PoseUtil.pose_inv(originRefFrame),
      pathPose1
    );
    // Get current pose in origin frame
    const currentPoseInOriginFrame = PoseUtil.pose_trans(
      PoseUtil.pose_inv(originRefFrame),
      newPose
    );
    // Calculate the offset of current pose to start pose
    const offsetPose = PoseUtil.pose_sub(
      currentPoseInOriginFrame,
      startPointInOriginFrame
    );
    if (isPathRotated) {
      offsetPose[1] = -offsetPose[1];
    }
    offsetPose[0] = round(offsetPose[0] * 1000, 1);
    offsetPose[1] = round(offsetPose[1] * 1000, 1);
    offsetPose[2] = round(offsetPose[2] * 1000, 1);
    return offsetPose;
  }
}
